/**
 * Pure helpers for the inactivity auto-logout feature.
 *
 * Two concerns, both kept side-effect-free so they can be unit-tested without
 * touching the inactivity clock, auth, or the DOM:
 *
 *  1. `resolveInactivityEnabled` — the enabled predicate (design §10).
 *     `useIdleLogout` runs the shared inactivity clock only while it holds, so
 *     the clock is inert (no events bound, no logout) when the feature is off,
 *     the session is unauthenticated, or we're in the test env without the
 *     dedicated E2E override.
 *  2. `resolveInactivityTiming` — parse the timing env vars into the two values
 *     the clock (`lib/inactivityClock.ts`) derives its instants from. The model:
 *       • idle-before-warning: how long of inactivity before the "Are you still
 *         here?" dialog appears.
 *       • countdown (prompt): how long the dialog counts down once shown — a
 *         FIXED window (default 60s), sitting ON TOP of the idle period rather
 *         than carved out of it.
 *     So `timeoutMs` (hard logout) = idleBeforeWarning + countdown, and
 *     `promptMs` = countdown. The default is 4:00 idle + 60s countdown
 *     = 5:00 total, the SCA-RTS Art 4(3)(d) ceiling (design §3/§4) — keep
 *     idle-before-warning ≤ 4:00 in production so the total stays ≤ 5:00.
 *
 * The values are read from `NEXT_PUBLIC_*` env vars (build-time inlined). The
 * raw reads live in `readInactivityConfig`; the parsing/predicate logic is
 * pure and exported for the truth-table test.
 *
 * Deployed-env test knob: a plain `IDLE_TIMEOUT` (in SECONDS) set in e.g. UAT is
 * bridged in `next.config.mjs` to `NEXT_PUBLIC_IDLE_TIMEOUT_MS` (× 1000) so it
 * reaches the client bundle. It sets the idle-before-warning period only — the
 * popup still counts down the fixed 60s once shown. Takes precedence over a
 * directly-set `NEXT_PUBLIC_IDLE_TIMEOUT_MS`.
 */

/**
 * Default inactivity before the warning dialog appears (4:00). With the fixed
 * 60s countdown on top this gives a 5:00 total — the SCA-RTS Art 4(3)(d) ceiling.
 */
export const DEFAULT_IDLE_BEFORE_WARNING_MS = 240_000;
/** Default countdown shown once the warning appears (60s). Fixed window. */
export const DEFAULT_IDLE_PROMPT_MS = 60_000;

export interface InactivityEnabledInput {
  /** `NEXT_PUBLIC_INACTIVITY_LOGOUT_DISABLED === "true"` — deployed kill-switch. */
  disabledFlag: boolean;
  /** `isTestEnv` from `lib/env.ts`. */
  isTestEnv: boolean;
  /** `NEXT_PUBLIC_INACTIVITY_LOGOUT_E2E === "true"` — force-enable in the E2E build. */
  e2eOverride: boolean;
  /** `auth.isAuthenticated` from `react-oidc-context`. */
  isAuthenticated: boolean;
}

/**
 * The enabled predicate (design §10):
 *
 *   enabled = !disabledFlag && (!isTestEnv || e2eOverride) && isAuthenticated
 *
 * On by default in production, off in the test env unless the dedicated E2E
 * build force-enables it, off entirely when signed out or kill-switched.
 */
export function resolveInactivityEnabled({
  disabledFlag,
  isTestEnv,
  e2eOverride,
  isAuthenticated,
}: InactivityEnabledInput): boolean {
  return !disabledFlag && (!isTestEnv || e2eOverride) && isAuthenticated;
}

/**
 * Parse a positive-integer millisecond env var. Returns `fallback` for any
 * unset / non-numeric / non-integer / non-positive value (no silent 0 timer).
 */
export function parsePositiveIntEnv(
  raw: string | undefined,
  fallback: number
): number {
  if (raw === undefined || raw.trim() === "") return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
    return fallback;
  }
  return parsed;
}

export interface InactivityTiming {
  /** Hard-logout timeout in ms = idle-before-warning + countdown. */
  timeoutMs: number;
  /** Countdown window in ms shown once the warning appears. */
  promptMs: number;
}

/**
 * Resolve the timer timing. `idleBeforeWarningRaw` is how long of inactivity
 * before the warning appears; `promptRaw` is the fixed countdown shown once it
 * does. The hard-logout `timeoutMs` is their SUM — the countdown sits ON TOP of
 * the idle period, not carved out of it — and `promptMs` is the countdown.
 * Because `timeoutMs = idle + countdown` with `idle ≥ 1ms`, `promptMs` is
 * always strictly < `timeoutMs`, so no clamp is needed.
 */
export function resolveInactivityTiming({
  idleBeforeWarningRaw,
  promptRaw,
}: {
  idleBeforeWarningRaw: string | undefined;
  promptRaw: string | undefined;
}): InactivityTiming {
  const idleBeforeWarningMs = parsePositiveIntEnv(
    idleBeforeWarningRaw,
    DEFAULT_IDLE_BEFORE_WARNING_MS
  );
  const promptMs = parsePositiveIntEnv(promptRaw, DEFAULT_IDLE_PROMPT_MS);

  return { timeoutMs: idleBeforeWarningMs + promptMs, promptMs };
}

export interface InactivityConfig {
  disabledFlag: boolean;
  e2eOverride: boolean;
  timing: InactivityTiming;
}

/**
 * Test-only runtime enable. The build-time `NEXT_PUBLIC_INACTIVITY_LOGOUT_E2E`
 * flag requires a dedicated build (a `NEXT_PUBLIC_*` var is inlined at build
 * time). This lets a single E2E spec opt into the feature at runtime — by
 * setting `window.__INACTIVITY_LOGOUT_E2E__` before app boot via
 * `page.addInitScript` — so inactivity can be exercised with Playwright's fake
 * clock in the normal `bun run test:e2e` suite, no special build needed.
 *
 * Honored ONLY when `isTestEnv` is true, so production never reads the flag. It
 * would be inert there anyway: the predicate's `(!isTestEnv || e2eOverride)`
 * short-circuits on `!isTestEnv` in production, so `e2eOverride` only ever
 * changes the result inside the test env. The `isTestEnv` guard makes that
 * test-only intent explicit rather than relying on that short-circuit.
 */
function readRuntimeE2EOverride(): boolean {
  if (process.env.NEXT_PUBLIC_IS_TEST_ENV !== "true") return false;
  if (typeof window === "undefined") return false;
  return (
    (window as unknown as { __INACTIVITY_LOGOUT_E2E__?: boolean })
      .__INACTIVITY_LOGOUT_E2E__ === true
  );
}

/**
 * Read the inactivity feature config from `NEXT_PUBLIC_*` env vars. Kept thin
 * so the pure predicate/timing functions above carry the testable logic.
 * `NEXT_PUBLIC_IDLE_TIMEOUT_MS` is the idle-before-warning period (the popup
 * then counts down `NEXT_PUBLIC_IDLE_PROMPT_MS` on top). `e2eOverride` is true
 * for the build-time flag OR the test-only runtime flag (see above).
 */
export function readInactivityConfig(): InactivityConfig {
  return {
    disabledFlag: process.env.NEXT_PUBLIC_INACTIVITY_LOGOUT_DISABLED === "true",
    e2eOverride:
      process.env.NEXT_PUBLIC_INACTIVITY_LOGOUT_E2E === "true" ||
      readRuntimeE2EOverride(),
    timing: resolveInactivityTiming({
      idleBeforeWarningRaw: process.env.NEXT_PUBLIC_IDLE_TIMEOUT_MS,
      promptRaw: process.env.NEXT_PUBLIC_IDLE_PROMPT_MS,
    }),
  };
}
